import { useEffect, useState } from "react";
import Notepad from "../components/notepad";
import NavBar from "../components/nav_bar";
import { usePicturesStore, pictures } from "../store/pictures";
import SystemMessage from "../components/system_message";
import t from "../locales";
import youFindIcon from "../assets/images/you_find.png";
import dragIcon from "../assets/images/you_find/drag_icon.svg";
import emptyIcon from "../assets/images/you_find/empty_icon.svg";
import cogWheelLeft from "../assets/images/you_find/cogwheel_1.svg";
import cogWheelRight from "../assets/images/you_find/cogwheel_2.svg";
import SaveInfoTooltip from "../components/save_info_tooltip";

function MainInfoProcessing() {
  return (
    <div className="you-find__main-overlay">
      <img
        className="you-find__main-cogwheel you-find__main-cogwheel--left"
        src={cogWheelLeft}
        alt=""
      />
      <img
        className="you-find__main-cogwheel you-find__main-cogwheel--right"
        src={cogWheelRight}
        alt=""
      />
    </div>
  );
}

function MainInfoLocationFound({ shownPicture, onClick }) {
  return (
    <>
      <div className="you-find__main-overlay">
        <div className="you-find__orange-frame">
          <p className="you-find__orange-frame-title">
            {t("youfind_location_found")}
          </p>
          <div>
            <p className="you-find__orange-frame-text">
              {shownPicture.foundLocation?.street}
            </p>
            <SaveInfoTooltip className="you-find--tooltip" name="city" dir="up">
              <p className="you-find__orange-frame-text">
                {t("secrets_city_value")} {shownPicture.foundLocation?.cityname}
              </p>
            </SaveInfoTooltip>
          </div>
          <button onClick={onClick} className="you-find__orange-frame-button">
            {t("youfind_ok")}
          </button>
        </div>
      </div>
    </>
  );
}

function MainInfoLocationNotFound({ onClick }) {
  return (
    <div className="you-find__main-overlay">
      <div className="you-find__orange-frame">
        <p className="you-find__orange-frame-title">
          {t("youfind_location_not_found")}
        </p>
        <button onClick={onClick} className="you-find__orange-frame-button">
          {t("youfind_try_again")}
        </button>
      </div>
    </div>
  );
}

export default function YouFind() {
  const STATES = {
    IDLE: 0,
    PROCESSING: 1,
    LOCATION_NOT_FOUND: 2,
    LOCATION_FOUND: 3,
  };
  const [state, setState] = useState(STATES.IDLE);
  const saved = usePicturesStore((state) => state.savedPictures);
  const savedPictures = pictures.filter((p) => saved.includes(p.key));
  const [dragSourcePicture, setDragSourcePicture] = useState(null);
  const [shownPicture, setShownPicture] = useState(null);
  const empty = savedPictures.length == 0;

  function handleTouchEnd(event) {
    if (event.changedTouches?.length) {
      const targetCollection = document.elementsFromPoint(
        event.changedTouches[0].clientX,
        event.changedTouches[0].clientY,
      );
      const dropzone = [...targetCollection].find((el) =>
        el.classList.contains("you-find__main"),
      );
      if (dropzone) setShownPicture(dragSourcePicture);
    }
  }

  useEffect(() => {
    setState(STATES.IDLE);
    if (shownPicture === null) return;

    setTimeout(
      () => {
        setState(STATES.PROCESSING);
        setTimeout(
          () =>
            setState(
              "foundLocation" in shownPicture
                ? STATES.LOCATION_FOUND
                : STATES.LOCATION_NOT_FOUND,
            ),
          Math.floor(Math.random() * 600) + 1200,
        );
      },
      Math.floor(Math.random() * 300) + 300,
    );
  }, [shownPicture]);

  return (
    <div className="you-find">
      {empty ? (
        <SystemMessage
          text={t("youfind_system_message_empty")}
          type="notAllowed"
        />
      ) : (
        <SystemMessage text={t("youfind_system_message_instructions")} />
      )}

      <NavBar icon={youFindIcon} />
      {empty ? (
        <div className="you-find__main--empty">
          <img className="you-find__main--empty-image" src={emptyIcon} alt="" />
        </div>
      ) : (
        <div
          className="you-find__main"
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={() => {
            setShownPicture(dragSourcePicture);
          }}
        >
          {shownPicture == null && (
            <div className="you-find__main-drag">
              <img className="you-find__drag-icon" src={dragIcon} alt="" />
              <p className="you-find__drag-text">{t("youfind_drag")}</p>
            </div>
          )}
          {shownPicture != null && (
            <img className="you-find__main-picture" src={shownPicture.file} />
          )}
          {state == STATES.PROCESSING && <MainInfoProcessing />}
          {shownPicture != null && state == STATES.LOCATION_FOUND && (
            <MainInfoLocationFound
              shownPicture={shownPicture}
              onClick={() => {
                setShownPicture(null);
                setDragSourcePicture(null);
              }}
            />
          )}
          {shownPicture != null && state == STATES.LOCATION_NOT_FOUND && (
            <MainInfoLocationNotFound
              onClick={() => {
                setShownPicture(null);
                setDragSourcePicture(null);
              }}
            />
          )}
        </div>
      )}
      <div className="you-find__slider">
        {savedPictures.map((p) => (
          <button
            draggable
            className="you-find__picture"
            id={p.key}
            key={p.key}
            onDragStart={() => setDragSourcePicture(p)}
            onTouchStart={() => setDragSourcePicture(p)}
            onTouchEnd={handleTouchEnd}
          >
            <img src={p.file} alt="" />
            <div className="you-find__picture-info">
              <p className="you-find_picture-info-text">{p.filename}</p>
              <br />
              <p className="you-find_picture-info-text">{p.date}</p>
              <p className="you-find_picture-info-text">{p.camera}</p>
              <p className="you-find_picture-info-text">{p.filesize}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="you-find__pictures-saved">
        {savedPictures.length}{" "}
        {savedPictures.length == 1
          ? t("youfind_picture_saved")
          : t("youfind_pictures_saved")}
      </div>
      <Notepad />
    </div>
  );
}
